import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaCalendarAlt, FaUserTie, FaUsers, FaPlus, FaSearch,
    FaCheckCircle, FaTimesCircle, FaStar, FaHistory, FaTrash, FaEdit,
    FaChevronLeft, FaChevronRight
} from "react-icons/fa";
import PatrolActivityModal from "./PatrolActivityModal";

const PatrolRosterManagementPage = () => {
    const [rosters, setRosters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMember, setSelectedMember] = useState(null);

    // --- حالات الترقيم الصفحي (Pagination States) ---
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // حالات المودال والنشاط
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [selectedActivityData, setSelectedActivityData] = useState(null);
    const [fetchingActivity, setFetchingActivity] = useState(false);

    // فلاتر البحث
    const [searchTerm, setSearchTerm] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const [filterStatus, setFilterStatus] = useState("active");

    const navigate = useNavigate();
    const { backEnd_request } = BackMethods();

    // جلب البيانات مع الترقيم والفلترة من السيرفر
    const fetchRosters = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                size: pageSize,
                status: filterStatus,
                date: filterDate || null
            };

            const response = await backEnd_request.get("/PatrolRosters", { params });

            // تحديث البيانات بناءً على هيكلة JSON المعتادة (total, data)
            setRosters(response.data.data || []);
            setTotalItems(response.data.total || 0);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterStatus, filterDate, backEnd_request]);

    useEffect(() => {
        fetchRosters();
    }, [fetchRosters]);

    // حساب إجمالي الصفحات
    const totalPages = Math.ceil(totalItems / pageSize);

    // الفلترة المحلية للبحث السريع (الاسم أو المسؤول)
    const filteredRosters = rosters.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        const adminName = `${item.user.firstName} ${item.user.lastName}`.toLowerCase();
        const membersNames = item.members.map(m => m.firstName.toLowerCase()).join(" ");

        return searchTerm === "" ||
            adminName.includes(searchLower) ||
            membersNames.includes(searchLower) ||
            item.patrolDate.includes(searchTerm);
    });

    const handleViewActivity = async (roster) => {
        setFetchingActivity(true);
        try {
            const response = await backEnd_request.get(`/admin/reports/activity/by-roster/${roster.patrolRosterId}`);
            setSelectedActivityData(response.data);
            setShowActivityModal(true);
        } catch (error) {
            alert("فشل في جلب بيانات النشاط");
        } finally {
            setFetchingActivity(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("هل أنت متأكد من حذف هذه الدورية؟")) return;
        try {
            await backEnd_request.delete(`/PatrolRosters/${id}`);
            fetchRosters();
        } catch (error) {
            console.error("Delete error:", error);
        }
    };

    return (
        <div className="container-fluid px-lg-5 py-4" dir="rtl" style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>

            {/* Header Section */}
            <div className="row mb-4 align-items-center gy-3">
                <div className="col-12 col-md-6">
                    <h2 className="fw-bold text-dark mb-1">
                        <FaCalendarAlt className="ms-2 text-primary" /> سجلات الدوريات
                    </h2>
                    <p className="text-muted mb-0 small">تنظيم ومتابعة طاقم العمليات الميدانية (إجمالي: {totalItems})</p>
                </div>
                <div className="col-12 col-md-6 text-md-end d-flex flex-wrap gap-2 justify-content-md-end">
                    <button
                        className="btn btn-outline-primary shadow-sm px-4 rounded-3 fw-bold d-inline-flex align-items-center"
                        onClick={() => navigate("/admin/patrol-activity-matrix")}
                    >
                        <FaHistory className="ms-2" /> نشاط الدوريات
                    </button>
                    <button
                        className="btn btn-primary shadow-sm px-4 rounded-3 fw-bold d-inline-flex align-items-center"
                        onClick={() => navigate("/admin/patrol-rosters/create")}
                    >
                        <FaPlus className="ms-2" /> إضافة دورية جديدة
                    </button>
                </div>
            </div>

            {/* Advanced Filters Bar */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="input-group shadow-sm rounded-3 overflow-hidden">
                        <span className="input-group-text bg-white border-0"><FaSearch className="text-muted" /></span>
                        <input
                            type="text"
                            className="form-control border-0 py-2"
                            placeholder="بحث سريع..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="input-group shadow-sm rounded-3 overflow-hidden">
                        <span className="input-group-text bg-white border-0 text-muted small">التاريخ:</span>
                        <input
                            type="date"
                            className="form-control border-0 py-2"
                            value={filterDate}
                            onChange={(e) => { setFilterDate(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>

                <div className="col-md-2">
                    <div className="input-group shadow-sm rounded-3 overflow-hidden">
                        <select
                            className="form-select border-0 py-2"
                            value={filterStatus}
                            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="active">نشطة</option>
                            <option value="unactive">منتهية</option>
                            <option value="all">الكل</option>
                        </select>
                    </div>
                </div>

                <div className="col-md-2">
                    <select
                        className="form-select border-0 shadow-sm py-2 rounded-3"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value={10}>10 سجلات</option>
                        <option value={20}>20 سجل</option>
                        <option value={50}>50 سجل</option>
                    </select>
                </div>

                <div className="col-md-2 text-center bg-white rounded-3 shadow-sm border-start border-primary border-4 py-2">
                    <span className="d-block fw-bold text-primary fs-5">{totalItems}</span>
                    <small className="text-muted x-small">إجمالي السجلات</small>
                </div>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : (
                <>
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="card-body p-0">
                            <div className="table-responsive d-none d-lg-block">
                                <table className="table table-hover align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr className="text-secondary small">
                                            <th className="ps-4 py-3">التاريخ</th>
                                            <th>المسؤول المباشر</th>
                                            <th>طاقم الدورية</th>
                                            <th>الحالة</th>
                                            <th className="text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRosters.map((item) => (
                                            <tr key={item.patrolRosterId}>
                                                <td className="ps-4 fw-bold text-dark">{item.patrolDate}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="avatar-sm bg-soft-primary text-primary rounded-circle ms-2 d-flex align-items-center justify-content-center fw-bold">
                                                            {item.user.firstName[0]}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold small">{item.user.firstName} {item.user.lastName}</div>
                                                            <div className="text-muted x-small">@{item.user.username}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {item.members.map((m) => (
                                                            <span
                                                                key={m.patrolRosterMemberId}
                                                                className={`badge roster-badge ${m.isLeader ? 'leader' : 'member'}`}
                                                                onClick={() => setSelectedMember(m)}
                                                                data-bs-toggle="modal" data-bs-target="#memberModal"
                                                            >
                                                                {m.isLeader && <FaStar className="ms-1" size={10} />}
                                                                {m.firstName}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>
                                                    {item.isActive ?
                                                        <span className="badge-status active"><FaCheckCircle className="ms-1" /> نشطة</span> :
                                                        <span className="badge-status inactive"><FaTimesCircle className="ms-1" /> منتهية</span>
                                                    }
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <button className="btn btn-sm btn-action text-info" title="عرض النشاط" onClick={() => handleViewActivity(item)}>
                                                            <FaHistory size={18} />
                                                        </button>
                                                        <button className="btn btn-sm btn-action text-primary" onClick={() => navigate(`/admin/patrol-rosters/edit/${item.patrolRosterId}`)}>
                                                            <FaEdit size={16} />
                                                        </button>
                                                        <button className="btn btn-sm btn-action text-danger" onClick={() => handleDelete(item.patrolRosterId)}>
                                                            <FaTrash size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile View */}
                            <div className="d-lg-none p-3">
                                {filteredRosters.map((item) => (
                                    <div key={item.patrolRosterId} className="card border shadow-sm rounded-3 mb-3">
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="fw-bold text-primary">{item.patrolDate}</span>
                                                {item.isActive ? <span className="text-success small">● نشطة</span> : <span className="text-danger small">● منتهية</span>}
                                            </div>
                                            <div className="mb-3">
                                                <span className="fw-semibold small">{item.user.firstName} {item.user.lastName}</span>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-outline-info btn-sm flex-grow-1" onClick={() => handleViewActivity(item)}>نشاط</button>
                                                <button className="btn btn-outline-primary btn-sm" onClick={() => navigate(`/admin/patrol-rosters/edit/${item.patrolRosterId}`)}><FaEdit /></button>
                                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(item.patrolRosterId)}><FaTrash /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* --- Pagination Controls --- */}
                    {totalPages > 1 && (
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 bg-white p-3 rounded-4 shadow-sm gap-3">
                            <div className="text-muted small">
                                عرض الصفحة <b>{currentPage}</b> من أصل <b>{totalPages}</b>
                            </div>
                            <nav>
                                <ul className="pagination mb-0 gap-1">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link rounded-circle border-0 shadow-sm" onClick={() => setCurrentPage(p => p - 1)}>
                                            <FaChevronRight size={12} />
                                        </button>
                                    </li>
                                    {[...Array(totalPages)].map((_, i) => {
                                        const n = i + 1;
                                        if (n === 1 || n === totalPages || (n >= currentPage - 1 && n <= currentPage + 1)) {
                                            return (
                                                <li key={n} className={`page-item ${currentPage === n ? 'active' : ''}`}>
                                                    <button className="page-link rounded-pill border-0 mx-1 shadow-sm" onClick={() => setCurrentPage(n)}>{n}</button>
                                                </li>
                                            );
                                        } else if (n === currentPage - 2 || n === currentPage + 2) {
                                            return <li key={n} className="align-self-end px-1 text-muted">...</li>;
                                        }
                                        return null;
                                    })}
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button className="page-link rounded-circle border-0 shadow-sm" onClick={() => setCurrentPage(p => p + 1)}>
                                            <FaChevronLeft size={12} />
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </>
            )}

            <PatrolActivityModal show={showActivityModal} onHide={() => setShowActivityModal(false)} data={selectedActivityData} />

            {/* Member Info Modal (Bootstrap) */}
            <div className="modal fade" id="memberModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-sm">
                    <div className="modal-content border-0 shadow-lg rounded-4 text-center">
                        <div className="modal-body p-4">
                            {selectedMember && (
                                <>
                                    <div className={`avatar-lg mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center ${selectedMember.isLeader ? 'bg-warning-subtle text-warning' : 'bg-primary-subtle text-primary'}`} style={{ width: 70, height: 70 }}>
                                        <FaUserTie size={30} />
                                    </div>
                                    <h5 className="fw-bold mb-1">{selectedMember.firstName} {selectedMember.lastName}</h5>
                                    <p className="text-muted small mb-3">{selectedMember.departmentName || "بدون قسم"}</p>
                                    <button className="btn btn-dark w-100 rounded-pill" data-bs-dismiss="modal">إغلاق</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .bg-soft-primary { background-color: #e7f1ff; }
                .x-small { font-size: 0.75rem; }
                .roster-badge { cursor: pointer; padding: 6px 10px; border-radius: 6px; transition: 0.2s; font-weight: 500; font-size: 0.8rem; }
                .roster-badge.leader { background-color: #fff9db; color: #856404; border: 1px solid #ffeeba; }
                .roster-badge.member { background-color: #f8f9fa; color: #495057; border: 1px solid #e9ecef; }
                .badge-status { padding: 5px 12px; border-radius: 20px; font-size: 0.75rem; font-weight: bold; }
                .badge-status.active { background-color: #d4edda; color: #155724; }
                .badge-status.inactive { background-color: #f8d7da; color: #721c24; }
                .btn-action { background: none; border: none; padding: 5px 10px; border-radius: 5px; transition: 0.2s; }
                .btn-action:hover { background-color: #f1f1f1; }
                .page-item.active .page-link { background-color: #0d6efd; color: white; }
                .page-link { color: #0d6efd; }
            `}</style>
        </div>
    );
};

export default PatrolRosterManagementPage;